import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { AppSessionService } from "../app-session/app-session.service";

import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import { setAdvancedFilterQuery, setSimpleFilterQuery } from "../../utils/tableUtils";
import { GridUtils } from "../../utils/gridUtils";

const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class PosMovementsService {
  private DOCUMENTS_URL = this.appSession.baseUrl + "/api/v1/documents";
  private DOCUMENTS_STATUS_URL = this.appSession.baseUrl + "/api/v1/documents/status";
  private DOCUMENTS_BUILDER_URL = this.appSession.baseUrl + "/api/v1/documentBuilder";
  private DOCUMENTS_BUILDER_SAVE_URL = this.appSession.baseUrl + "/api/v1/documentBuilder/save";
  private DOCUMENTS_PRINT = this.appSession.baseUrl + "/api/v1/documents";
  
  constructor(
    private http: HttpClient,
    private appSession: AppSessionService
  ) {}

  getDocuments(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    const url = paging
      ? this.buildDataUrl(paging, "?")
      : `${this.DOCUMENTS_URL}`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getDocumentHeaders(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    const url = paging
      ? this.buildDataUrl(paging, "/headers?")
      : `${this.DOCUMENTS_URL}/headers`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getDocumentLines(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    const url = paging
      ? this.buildDataUrl(paging, "/lines?")
      : `${this.DOCUMENTS_URL}/lines`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getDocumentDetails(code: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
  
    const url = `${this.DOCUMENTS_URL}/${code}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getDocumentStatuses(): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.DOCUMENTS_STATUS_URL}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  getDocumentBuilderOut(token: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.DOCUMENTS_BUILDER_URL}/${token}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  updateDocumentBuilderOutSave(draftDocumentOut): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const data = JSON.stringify(draftDocumentOut);
    const url = `${this.DOCUMENTS_BUILDER_SAVE_URL}`;
    return this.http.put<any>(url, data, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  printDocument(documentToken: string) {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
      "user_name": "administrator"
    });

    // const tokens = {
    //   tokens: [ documentToken ]
    // };

    // const data = JSON.stringify(tokens);
    const url = `${this.DOCUMENTS_PRINT}/${documentToken}/printA4`;
    // return this.http.put(url, { headers });
    // return this.http.put<any>(`http://192.168.203.89:51006/api/v1/documents/${documentToken}/print`, { headers }).pipe(
    //   map(({ result }) => result),
    //   catchError((error) => throwError(error))
    // );;
    return this.http.put<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }
  
  getDocumentClosed(documentCode: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.DOCUMENTS_URL}/${documentCode}`;
    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  billDocument(billPalyoad: any, customerPartyVatin: string): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const data = JSON.stringify(billPalyoad);

    const url = `${this.DOCUMENTS_BUILDER_URL}/aggregateDraftForBilling/${customerPartyVatin}`;

    return this.http.put<any>(url, data, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }
  
  // Grid filter helpers block
  private buildDataUrl(pagingArgs: any, path: string): string {
    let baseQueryString = `${this.DOCUMENTS_URL}${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query += scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;
    baseQueryString += query;

    return baseQueryString;
  }
}
